import React, { useEffect, useState } from "react";
import { X, Save, Loader2, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { STATI, TIPI, DISTRIBUTORI, GAUDI_URL, EMPTY, inputCls, inputStyle, lblCls } from "./praticheConst";

const Field = ({ label, children, span }) => (
  <div className={span ? "md:col-span-2" : ""}>
    <label className={lblCls} style={{ color: "var(--text-muted)" }}>{label}</label>
    {children}
  </div>
);

const GaudiBox = ({ form, set }) => (
  <div className="md:col-span-2 p-3 rounded-lg border space-y-2" style={{ borderColor: "#EF6C00", background: "#FFF3E0" }} data-testid="pratica-gaudi-box">
    <div className="flex items-start gap-2 text-sm" style={{ color: "#8D3B00" }}>
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>Pratica ordinaria: va aperta anche su <b>Gaudì di Terna</b>.</span>
      <a href={GAUDI_URL} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-xs font-semibold underline" data-testid="pratica-gaudi-link">Apri Gaudì <ExternalLink className="w-3 h-3" /></a>
    </div>
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.gaudi_aperta} onChange={(e) => set({ gaudi_aperta: e.target.checked })} className="w-4 h-4" data-testid="pratica-gaudi-aperta" /> Pratica aperta su Gaudì</label>
    <input value={form.codice_gaudi} onChange={(e) => set({ codice_gaudi: e.target.value })} placeholder="Codice impianto / pratica Gaudì" className={inputCls} style={inputStyle} data-testid="pratica-codice-gaudi" />
  </div>
);

export const PraticaForm = ({ cid, cliente, pratica, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...EMPTY, commessa_id: cliente?.commessa_id || "", ...(pratica || {}) });
  const [commesse, setCommesse] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  useEffect(() => { api.get("/commesse").then(({ data }) => setCommesse(data || [])).catch(() => {}); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k]]));
      const { data } = pratica ? await api.put(`/pratiche-allaccio/${pratica.id}`, body) : await api.post(`/clienti/${cid}/pratiche-allaccio`, body);
      toast.success(pratica ? "Pratica aggiornata" : "Pratica creata"); onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const inp = (k, props = {}) => <input value={form[k]} onChange={(e) => set({ [k]: e.target.value })} className={inputCls} style={inputStyle} data-testid={`pratica-${k}`} {...props} />;

  return (
    <div className="fixed inset-0 !mt-0 z-50 flex items-end md:items-center justify-center md:p-4" style={{ background: "rgba(0,0,0,.45)" }} data-testid="pratica-form">
      <div className="bg-white w-full max-w-2xl rounded-t-2xl md:rounded-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center gap-2 p-4 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="font-bold flex-1">{pratica ? "Modifica pratica allaccio" : "Nuova pratica allaccio"}</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5" data-testid="pratica-form-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-auto grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Soggetto titolare" span>{inp("soggetto_titolare", { placeholder: "Intestatario della pratica" })}</Field>
          <Field label="Codice pratica">{inp("codice_pratica")}</Field>
          <Field label="POD">{inp("pod", { placeholder: "IT001E…" })}</Field>
          <Field label="Data assegnazione">{inp("data_assegnazione", { type: "date" })}</Field>
          <Field label="Data inoltro">{inp("data_inoltro", { type: "date" })}</Field>
          <Field label="Tipo pratica">
            <select value={form.tipo_pratica} onChange={(e) => set({ tipo_pratica: e.target.value })} className={inputCls} style={inputStyle} data-testid="pratica-tipo_pratica">
              {Object.entries(TIPI).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Stato della pratica">
            <select value={form.stato} onChange={(e) => set({ stato: e.target.value })} className={inputCls} style={inputStyle} data-testid="pratica-stato">
              {STATI.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          {form.tipo_pratica === "ordinaria" && <GaudiBox form={form} set={set} />}
          <Field label="Commessa">
            <select value={form.commessa_id} onChange={(e) => set({ commessa_id: e.target.value })} className={inputCls} style={inputStyle} data-testid="pratica-commessa_id">
              <option value="">— Commessa del cliente —</option>
              {commesse.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Portale distributore">
            {inp("portale_distributore", { list: "distributori-list", placeholder: "Es. E-Distribuzione" })}
            <datalist id="distributori-list">{DISTRIBUTORI.map((d) => <option key={d} value={d} />)}</datalist>
          </Field>
          <Field label="Note" span>
            <textarea value={form.note} onChange={(e) => set({ note: e.target.value })} rows={2} className={inputCls} style={inputStyle} data-testid="pratica-note" />
          </Field>
        </div>
        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button onClick={onClose} className="btn-secondary" data-testid="pratica-form-cancel">Annulla</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="pratica-form-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva</button>
        </div>
      </div>
    </div>
  );
};
